// src/pages/Home.js
import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './Home.css';
import planosPdf from '../assets/docs/planos.pdf';

// Carrega automaticamente banner1, banner2, ... (jpeg/jpg/png/webp)
const bannerContext = require.context(
  '../assets/images',
  false,
  /^\.\/banner\d+\.(jpe?g|png|webp)$/i
);

const banners = bannerContext
  .keys()
  .sort((a, b) => {
    const numA = parseInt(a.match(/\d+/)[0], 10);
    const numB = parseInt(b.match(/\d+/)[0], 10);
    return numA - numB;
  })
  .map((key, index) => ({
    src: bannerContext(key),
    alt: `Banner promocional G3 Rastreamento ${index + 1}`
  }));

const Home = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  const prevBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  }, []);

  useEffect(() => {
    if (isPaused || isModalOpen || banners.length <= 1) return undefined;

    const timer = setInterval(nextBanner, 6500);
    return () => clearInterval(timer);
  }, [isPaused, isModalOpen, nextBanner]);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsModalOpen(false);
      if (e.key === 'ArrowRight') nextBanner();
      if (e.key === 'ArrowLeft') prevBanner();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, nextBanner, prevBanner]);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;

    const currentTouch = e.touches[0].clientX;
    const diff = touchStart - currentTouch;

    if (Math.abs(diff) > 50) {
      if (diff > 0) nextBanner();
      else prevBanner();
      setTouchStart(0);
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(0);
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <h1 className="hero-title">Segurança e Tranquilidade para seu Veículo</h1>
        <p className="hero-subtitle">
          Proteja seu veículo com nossa solução completa de rastreamento e monitoramento
        </p>
        <div className="hero-buttons">
          <a href={planosPdf} download className="hero-button primary-button">
            Conheça Nossos Planos
            <FaArrowRight />
          </a>
          <a href="/scheduling" className="hero-button secondary-button">
            Agende uma Demonstração
          </a>
        </div>
      </section>

      {/* Banner Section */}
      <section
        className="banner-section"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        aria-label="Banners promocionais"
      >
        <div className="banner-container">
          <div
            className="banner-slider"
            style={{ transform: `translateX(-${currentBanner * 100}%)` }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {banners.map((banner, index) => (
              <div
                key={banner.src}
                className={`banner-image-container ${currentBanner === index ? 'is-active' : ''}`}
              >
                <img
                  src={banner.src}
                  alt={banner.alt}
                  className="banner-image"
                  onClick={() => setIsModalOpen(true)}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            className="banner-nav-button prev"
            onClick={prevBanner}
            aria-label="Banner anterior"
          >
            <FaChevronLeft />
          </button>

          <button
            type="button"
            className="banner-nav-button next"
            onClick={nextBanner}
            aria-label="Próximo banner"
          >
            <FaChevronRight />
          </button>

          <div className="banner-footer">
            <div className="banner-counter" aria-live="polite">
              {currentBanner + 1} / {banners.length}
            </div>
            <div className="banner-dots">
              {banners.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`banner-dot ${currentBanner === index ? 'active' : ''}`}
                  onClick={() => setCurrentBanner(index)}
                  aria-label={`Ir para banner ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modal para banner expandido */}
      {isModalOpen && (
        <div
          className="banner-modal active"
          onClick={() => setIsModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Visualização ampliada do banner"
        >
          <button
            type="button"
            className="banner-modal-nav banner-modal-prev"
            onClick={(e) => {
              e.stopPropagation();
              prevBanner();
            }}
            aria-label="Banner anterior"
          >
            <FaChevronLeft />
          </button>

          <div className="banner-modal-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={banners[currentBanner].src}
              alt={banners[currentBanner].alt}
              className="banner-modal-image"
            />
            <button
              type="button"
              className="banner-modal-close"
              onClick={() => setIsModalOpen(false)}
              aria-label="Fechar modal"
            >
              ×
            </button>
          </div>

          <button
            type="button"
            className="banner-modal-nav banner-modal-next"
            onClick={(e) => {
              e.stopPropagation();
              nextBanner();
            }}
            aria-label="Próximo banner"
          >
            <FaChevronRight />
          </button>
        </div>
      )}

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stat-item">
            <h2 className="stat-number">+10.000</h2>
            <p className="stat-label">Veículos Monitorados</p>
          </div>

          <div className="stat-item">
            <h2 className="stat-number">98%</h2>
            <p className="stat-label">Taxa de Recuperação</p>
          </div>

          <div className="stat-item">
            <h2 className="stat-number">24/7</h2>
            <p className="stat-label">Monitoramento</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
